import { io, Socket } from 'socket.io-client';
import type { SocketNamespace } from '@/types';
import { authService } from './AuthService';

// Map frontend namespace names to actual server-side Socket.IO namespace paths
const NAMESPACE_PATH_MAP: Partial<Record<SocketNamespace, string>> = {
  chat: 'chat-enhanced',   // Frontend 'chat' → Server '/chat-enhanced'
};

class SocketService {
  private sockets: Map<SocketNamespace, Socket> = new Map();
  private readonly baseURL: string;

  constructor() {
    if (import.meta.env.PROD) {
      this.baseURL = window.location.origin;
    } else {
      this.baseURL = 'http://localhost:3099';
    }
  }

  /**
   * Fetch a WS-Ticket from the server.
   * Tickets are single-use and expire in 30 seconds.
   */
  private async fetchTicket(): Promise<string> {
    const csrf = authService.getCSRFToken();

    const response = await fetch(`${this.baseURL}/api/auth/ws-ticket`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {})
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get WS ticket: ${response.status}`);
    }

    const data = await response.json();
    return data.ticket;
  }

  /**
   * Connect to a Socket.IO namespace.
   * Uses WS-Ticket auth only — no legacy fallback.
   */
  async connect(namespace: SocketNamespace): Promise<Socket> {
    // Return existing socket if already connected
    const existing = this.sockets.get(namespace);
    if (existing?.connected) {
      return existing;
    }

    // Clean up disconnected socket
    if (existing) {
      existing.removeAllListeners();
      existing.disconnect();
      this.sockets.delete(namespace);
    }

    // Get auth ticket — fail if unavailable
    const ticket = await this.fetchTicket();

    const serverPath = NAMESPACE_PATH_MAP[namespace] || namespace;
    const socket = io(`${this.baseURL}/${serverPath}`, {
      auth: { ticket },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      timeout: 20000,
      autoConnect: true,
      withCredentials: true,
    });

    // Handle reconnection with fresh ticket
    socket.on('connect_error', async (error) => {
      console.error(`[SocketService] Connection error on /${namespace}:`, error.message);

      // If auth error, try getting a fresh ticket
      if (error.message.includes('Authentication') || error.message.includes('ticket')) {
        try {
          const freshTicket = await this.fetchTicket();
          // Update the auth for next reconnection attempt
          (socket as any).auth = { ticket: freshTicket };
        } catch {
          console.error(`[SocketService] Failed to refresh ticket for /${namespace}`);
        }
      }
    });

    socket.on('connect', () => {
      console.log(`[SocketService] Connected to /${namespace}`);
    });

    socket.on('disconnect', (reason) => {
      console.log(`[SocketService] Disconnected from /${namespace}: ${reason}`);

      // On server-initiated disconnect, get fresh ticket for reconnect
      if (reason === 'io server disconnect' || reason === 'transport close') {
        this.fetchTicket().then(freshTicket => {
          (socket as any).auth = { ticket: freshTicket };
        }).catch(() => {
          // Will retry on next reconnect attempt via connect_error handler
        });
      }
    });

    socket.on('error', (error: any) => {
      console.error(`[SocketService] Error on /${namespace}:`, error);
    });

    // Handle recovery notifications from server
    socket.on('system:recovery_notice', (data: any) => {
      console.warn(`[SocketService] Recovery notice:`, data);
      // Dispatch event for UI to handle
      window.dispatchEvent(new CustomEvent('system:recovery', { detail: data }));
    });

    this.sockets.set(namespace, socket);
    return socket;
  }

  disconnect(namespace: SocketNamespace): void {
    const socket = this.sockets.get(namespace);
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      this.sockets.delete(namespace);
    }
  }

  disconnectAll(): void {
    this.sockets.forEach((socket) => {
      socket.removeAllListeners();
      socket.disconnect();
    });
    this.sockets.clear();
  }

  getSocket(namespace: SocketNamespace): Socket | undefined {
    return this.sockets.get(namespace);
  }

  isConnected(namespace: SocketNamespace): boolean {
    return this.sockets.get(namespace)?.connected ?? false;
  }

  emit(namespace: SocketNamespace, event: string, ...args: any[]): void {
    const socket = this.getSocket(namespace);
    if (!socket?.connected) {
      throw new Error(`Socket for /${namespace} not connected`);
    }
    socket.emit(event, ...args);
  }

  /**
   * Emit an event and wait for the server's acknowledgement callback.
   * Standard response format: { success: boolean, error?: string, [key]: any }
   * Throws on timeout or if the server returns { success: false }.
   */
  request<T = any>(
    namespace: SocketNamespace,
    event: string,
    data?: any,
    timeoutMs = 10000
  ): Promise<T> {
    const socket = this.getSocket(namespace);
    if (!socket?.connected) {
      return Promise.reject(new Error(`Socket for /${namespace} not connected`));
    }

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`[${namespace}] ${event}: timeout after ${timeoutMs}ms`));
      }, timeoutMs);

      socket.emit(event, data, (response: any) => {
        clearTimeout(timer);
        if (response && response.success === false) {
          reject(new Error(response.error || response.message || `${event} failed`));
        } else {
          resolve(response as T);
        }
      });
    });
  }

  on(namespace: SocketNamespace, event: string, callback: (...args: any[]) => void): void {
    const socket = this.getSocket(namespace);
    if (!socket) {
      throw new Error(`Socket for /${namespace} not connected`);
    }
    socket.on(event, callback);
  }

  off(namespace: SocketNamespace, event: string, callback?: (...args: any[]) => void): void {
    const socket = this.getSocket(namespace);
    if (socket) {
      callback ? socket.off(event, callback) : socket.off(event);
    }
  }
}

export const socketService = new SocketService();
